import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ClipboardList, Download, FileText, Save, Trash2 } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  MasterDetail,
  PageHeaderBand,
  Popover,
  PopoverMenu,
  PopoverMenuGroup,
  PopoverMenuItem,
  PopoverTrigger,
  Select,
  toast,
  useFullscreen,
  type MasterDetailItem,
} from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import { useCustomerPricelists, usePricelistByPartner } from '../api/queries'
import type { PricelistByPartner, PricelistSummary } from '../api/types'
import { BulkExportDrawer } from '../components/BulkExportDrawer'
import { PricelistDetail } from '../components/PricelistDetail'
import { ITEM_COUNT_BUCKETS } from '../utils/item-count-filter'
import {
  addSaved,
  defaultLabelFor,
  loadSaved,
  removeSaved,
  type SavedSearch,
} from '../utils/saved-searches'

/**
 * Sprint 01b Chunk F — `?partner=N` deep-link resolution.
 *
 * Three branches driven by `GET /api/pricelists/by-partner/{id}`:
 *
 * 1. `is_default: false` — the partner has a customer-specific override.
 *    Select that pricelist in the list and drop the query param.
 *
 * 2. `is_default: true` — the partner maps to the system default public
 *    pricelist (Sprint 01 D-2 excludes the default from the normal
 *    `/customer` list because it carries ~700 partner assignments).
 *    Inject a synthetic "Public Pricelist" list-item at the top of the
 *    MasterDetail list with a `Default` badge; selecting it renders
 *    `PricelistDetail` in `readOnly` mode with a banner pointing to
 *    Odoo backoffice.
 *
 * 3. 404 — partner doesn't exist, or exists with no pricelist assignment.
 *    Inline error banner at the list top. Param cleared.
 */

const SYNTHETIC_DEFAULT_PREFIX = 'default:'

interface ListRow extends MasterDetailItem {
  pricelist: PricelistSummary
  isSyntheticDefault?: boolean
}

export function CustomerPricelistsPage() {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [minItems, setMinItems] = useState<number | null>(null)
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const { fullscreen, toggle: toggleFullscreen } = useFullscreen()
  const [bulkExportOpen, setBulkExportOpen] = useState(false)

  // Saved searches — this module's only filter is the search string, so a
  // "saved filter" is just a named query. Same localStorage pattern as
  // open-orders/cofa-documents.
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(() => loadSaved())

  const navigate = useNavigate()
  // Untyped-search reader — the module router doesn't declare search schemas,
  // so we read defensively and validate inline. Sprint 01 D-8 uses only
  // `?partner=N`; future additions would move to validateSearch.
  const search = useSearch({ strict: false }) as { partner?: string | number }

  const partnerParam = search?.partner
  const partnerId = useMemo(() => {
    if (partnerParam === undefined || partnerParam === '') return null
    const n = Number(partnerParam)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [partnerParam])

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])

  const { data, isLoading, isError } = useCustomerPricelists(debounced)
  const pricelists = useMemo<PricelistSummary[]>(
    () => data?.pricelists ?? [],
    [data],
  )

  // Deep-link resolution. Only fires when `partnerId` is set; disabled
  // otherwise, so regular navigation doesn't pay for the extra request.
  const byPartner = usePricelistByPartner(partnerId)

  // Track which deep-link request we've already resolved so we don't
  // repeatedly reset selection or re-inject the default row.
  const [resolvedForPartnerId, setResolvedForPartnerId] = useState<number | null>(
    null,
  )
  const [injectedDefault, setInjectedDefault] = useState<PricelistByPartner | null>(
    null,
  )

  // Deep-link error is derived state — sourced from either an invalid
  // `?partner=` value (no network call) OR an observed resolution
  // error on the byPartner query. Earlier this was a useState set
  // from inside two effects; that triggered react-hooks/set-state-
  // in-effect. Derived form is simpler, and "dismiss" works by
  // clearing the URL param (below) so the derivation naturally
  // returns null rather than needing a separate dismissed flag.
  const deepLinkError = useMemo<string | null>(() => {
    if (partnerParam !== undefined && partnerId === null) {
      return `Invalid ?partner value: ${String(partnerParam)}`
    }
    if (partnerId === null) return null
    if (resolvedForPartnerId !== partnerId) return null
    if (!byPartner.isError) return null
    const err = byPartner.error
    if (err instanceof ApiError && err.status === 404) {
      return `Partner ${partnerId} not found or has no pricelist.`
    }
    return `Couldn't resolve pricelist for partner ${partnerId}. Try again.`
  }, [
    partnerParam,
    partnerId,
    resolvedForPartnerId,
    byPartner.isError,
    byPartner.error,
  ])

  // On successful resolution: apply the branch, advance the resolved
  // marker so the drop-query-param effect below runs, then stop.
  useEffect(() => {
    if (partnerId === null) return
    if (resolvedForPartnerId === partnerId) return
    if (byPartner.isLoading) return

    if (byPartner.isError) {
      // Error is surfaced via the derived `deepLinkError` above; we
      // only need to advance the marker so the drop-query-param effect
      // runs and the error derivation latches at the error form.
      //
      // This is a legitimate "setState in response to async external
      // state transitions" — the byPartner query's isError/isLoading
      // flipping is an event from outside React. Rule suppressed per
      // this specific call.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResolvedForPartnerId(partnerId)
      return
    }

    const resolved = byPartner.data
    if (!resolved) return

    setResolvedForPartnerId(partnerId)

    if (resolved.is_default) {
      setInjectedDefault(resolved)
      setSelectedId(`${SYNTHETIC_DEFAULT_PREFIX}${resolved.pricelist_id}`)
    } else {
      setInjectedDefault(null)
      setSelectedId(String(resolved.pricelist_id))
    }
  }, [
    partnerId,
    byPartner.isLoading,
    byPartner.isError,
    byPartner.data,
    resolvedForPartnerId,
  ])

  // Clear the query param once we've acted on it. Done as a separate effect
  // so it doesn't contend with the resolution effect above.
  useEffect(() => {
    if (partnerId === null && partnerParam === undefined) return
    if (resolvedForPartnerId !== partnerId) return
    // Drop partner from search.
    navigate({
      to: '.',
      search: (prev: Record<string, unknown> | undefined) => {
        if (!prev || !('partner' in prev)) return prev ?? {}
        const next = { ...prev }
        delete next.partner
        return next
      },
      replace: true,
    })
  }, [partnerId, partnerParam, resolvedForPartnerId, navigate])

  // Filtered ahead of the row-mapping below so the synthetic deep-linked
  // default-pricelist row (unshifted after this map) is never hidden by
  // the count filter — a `?partner=` link should always resolve.
  const filteredPricelists = useMemo<PricelistSummary[]>(
    () =>
      minItems === null
        ? pricelists
        : pricelists.filter((p) => p.item_count >= minItems),
    [pricelists, minItems],
  )

  const items = useMemo<ListRow[]>(() => {
    const rows: ListRow[] = filteredPricelists.map((p) => ({
      id: String(p.id),
      primary: p.name,
      secondary: m2oLabel(p.partner_id) ?? undefined,
      meta: (
        <>
          <span>{p.item_count.toLocaleString()} items</span>
          {!p.active ? (
            <>
              <span className="opacity-40">·</span>
              <span className="text-warning-text">inactive</span>
            </>
          ) : null}
        </>
      ),
      pricelist: p,
    }))

    if (injectedDefault) {
      // Prepend a synthetic row for the system default pricelist. Has a
      // recognizable id so `renderDetail` can render it read-only with a
      // system-default banner.
      rows.unshift({
        id: `${SYNTHETIC_DEFAULT_PREFIX}${injectedDefault.pricelist_id}`,
        primary: injectedDefault.name || 'Public Pricelist',
        secondary: injectedDefault.partner_name,
        meta: (
          <>
            <Chip size="xs" variant="warning">
              Default
            </Chip>
          </>
        ),
        pricelist: {
          id: injectedDefault.pricelist_id,
          name: injectedDefault.name || 'Public Pricelist',
          currency_id: false,
          active: true,
          partner_id: [
            injectedDefault.partner_id,
            injectedDefault.partner_name,
          ],
          item_count: 0,
        },
        isSyntheticDefault: true,
      })
    }

    return rows
  }, [filteredPricelists, injectedDefault])

  return (
    <div
      className={
        fullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-canvas'
          : 'flex h-full flex-col'
      }
    >
      <PageHeaderBand
        title="Customer Pricelists"
        description="Per-customer pricing, customer part numbers, and min-qty tiers."
      >
        <Button variant="secondary" size="sm" onClick={() => setBulkExportOpen(true)}>
          <Download size={14} /> Bulk export…
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void navigate({ to: '/pricelists/reviews' } as never)}
        >
          <ClipboardList size={14} /> Price reviews
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void navigate({ to: '/pricelists/letters' } as never)}
        >
          <FileText size={14} /> Price change letters
        </Button>
      </PageHeaderBand>

      {deepLinkError ? (
        <div className="border-b border-danger bg-danger-bg px-6 py-2 text-sm text-danger-text">
          {deepLinkError}{' '}
          <button
            type="button"
            onClick={() => {
              // Dismiss = clear the URL param. The derived error
              // depends on partnerParam/partnerId, so the banner
              // disappears naturally when the param is gone. More
              // correct than hiding the banner while leaving a bad
              // URL in place (hard-refresh would re-show it).
              navigate({
                to: '.',
                search: (prev: Record<string, unknown> | undefined) => {
                  if (!prev) return {}
                  const next = { ...prev }
                  delete next.partner
                  return next
                },
                replace: true,
              })
              setResolvedForPartnerId(null)
            }}
            className="ml-2 cursor-pointer text-xs underline"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-hidden p-4">
        {isError ? (
          <EmptyState
            variant="error"
            title="Couldn't load pricelists"
            description="The /api/pricelists/customer endpoint returned an error. Check that the middleware container is running the current build."
          />
        ) : !isLoading && minItems !== null && items.length === 0 ? (
          <EmptyState
            variant="no-match"
            title="No pricelists match this filter"
            description="Try lowering the minimum item count or clearing the search."
            action={
              <Button variant="secondary" size="sm" onClick={() => setMinItems(null)}>
                Clear filter
              </Button>
            }
          />
        ) : (
          <MasterDetail<ListRow>
            items={items}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onSearch={setQuery}
            searchValue={query}
            searchPlaceholder={
              isLoading
                ? 'Loading pricelists…'
                : minItems !== null
                  ? `Search ${filteredPricelists.length}/${pricelists.length}`
                  : `Search ${pricelists.length} pricelists`
            }
            toolbarExtra={
              <>
                <Select
                  aria-label="Minimum items"
                  title="Minimum items"
                  className="w-16 flex-shrink-0"
                  value={minItems === null ? '' : String(minItems)}
                  onChange={(e) =>
                    setMinItems(e.target.value === '' ? null : Number(e.target.value))
                  }
                >
                  {ITEM_COUNT_BUCKETS.map((b) => (
                    <option key={b.label} value={b.value === null ? '' : b.value}>
                      {b.label}
                    </option>
                  ))}
                </Select>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" iconOnly aria-label="Saved searches">
                      <Save size={14} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverMenu>
                    <PopoverMenuGroup label="Saved searches">
                      <PopoverMenuItem
                        icon={Save}
                        onSelect={() => {
                          const label = window.prompt('Name this search', defaultLabelFor(query))
                          if (!label) return
                          setSavedSearches(addSaved(label, query))
                          toast.success(`Saved "${label}"`)
                        }}
                      >
                        Save current search…
                      </PopoverMenuItem>
                      {savedSearches.length > 0 ? (
                        <ul className="mt-1 flex flex-col border-t border-border-subtle pt-1">
                          {savedSearches.map((s) => (
                            <li key={s.id} className="flex items-center">
                              <button
                                type="button"
                                onClick={() => setQuery(s.query)}
                                title={s.label}
                                className="min-w-0 flex-1 cursor-pointer truncate border-none bg-transparent px-3 py-1.5 text-left text-sm text-primary hover:bg-surface-hover"
                              >
                                {s.label}
                              </button>
                              <button
                                type="button"
                                aria-label={`Remove ${s.label}`}
                                onClick={() => setSavedSearches(removeSaved(s.id))}
                                className="mr-2 inline-flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
                              >
                                <Trash2 size={12} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </PopoverMenuGroup>
                  </PopoverMenu>
                </Popover>
              </>
            }
            autoSelectFirst
            renderDetail={(row) =>
              row.isSyntheticDefault ? (
                <PricelistDetail
                  pricelist={row.pricelist}
                  readOnly
                  banner={<DefaultPricelistBanner partnerName={row.pricelist.partner_id ? row.pricelist.partner_id[1] : ''} />}
                  fullscreen={fullscreen}
                  onToggleFullscreen={toggleFullscreen}
                />
              ) : (
                <PricelistDetail
                  pricelist={row.pricelist}
                  fullscreen={fullscreen}
                  onToggleFullscreen={toggleFullscreen}
                />
              )
            }
          />
        )}
      </div>

      <BulkExportDrawer open={bulkExportOpen} onOpenChange={setBulkExportOpen} />
    </div>
  )
}

/**
 * Sprint 09 Item 3 — customer-named default-pricelist banner. Mirrors
 * the Sprint 03 D-4 vendor side: names the customer so the user knows
 * why they landed on the default and can take action ("create a
 * customer-specific pricelist in Odoo to track negotiated pricing")
 * instead of being told only what NOT to do.
 */
function DefaultPricelistBanner({ partnerName }: { partnerName: string }) {
  const who = partnerName?.trim() ? partnerName : 'This customer'
  return (
    <div className="rounded-sm border border-warning bg-warning-bg px-3 py-2 text-sm text-warning-text">
      <strong>{who} uses the default public pricelist, not a customer-specific one.</strong>{' '}
      <span className="italic">
        Consider creating a customer-specific pricelist in Odoo to track negotiated pricing for this customer.
      </span>
    </div>
  )
}

function m2oLabel(m2o: PricelistSummary['partner_id']): string | null {
  if (!m2o) return null
  return m2o[1] ?? null
}
